import { useState } from 'react';
import { useT } from '../i18n/useLanguage';
import JoinModal from '../components/JoinModal';

export default function CampaignSection() {
  const t = useT();
  const [modalOpen, setModalOpen] = useState(false);

  // NOTE: this section reuses the outer "scene" chrome from
  // StorySection (top/bottom fork-spoon spacers, the cream main
  // panel and an inner content frame with a PNG background image).
  // The only intentional differences from StorySection:
  //   • the inner frame background is `section6_inner_frame.png`
  //     (an open recipe book, native 2746×1874 ≈ 1.465:1) instead
  //     of the rose-pink `register-frame-vector.png`;
  //   • the inner frame content is just TWO text columns split
  //     50/50 horizontally — one for each visible book page;
  //   • the ring-binder section-seam ornaments aren't reproduced
  //     since this is the last <main> section.
  return (
    <section
      id="campaign"
      // Mobile-specific scroll behaviour (mirrors Register /
      // Story / Features mobile):
      //   • `!min-h-screen` (important) forces the section to
      //     ≥ 100vh on phones. The `!` is required because
      //     `.snap-section { min-height: 80vh }` lives outside
      //     `@layer` in index.css and is declared after
      //     `@tailwind utilities`, so on source-order it would
      //     normally win over a plain `min-h-screen`.
      //   • The mobile title block further below (the
      //     `block lg:hidden order-first` div) carries its own
      //     `snap-start [scroll-margin-top:60px]` so when
      //     mandatory snap settles on mobile the TITLE's top
      //     lands just under the floating header — that way
      //     "title → frame → CTA → prop" stacks top-down from
      //     under the header, matching Figma 191:4961. The book
      //     frame itself is too far down the section to be a
      //     useful snap target.
      //   • `md:[scroll-margin-top:-100px]` is kept for desktop
      //     link-click scrolling (header anchor → smooth scroll
      //     into view uses this margin); on mobile scroll-margin
      //     lives on the title block instead, so the section has
      //     none below md.
      //   • Responsive horizontal padding `px-4 md:px-8 lg:px-12`
      //     so phones get more usable width (was `px-12` always).
      className="snap-section relative flex !min-h-screen w-full flex-col items-center bg-[#F6ebd6] px-4 md:px-8 lg:px-12 md:[scroll-margin-top:-100px]"
      style={{
        // Same decorative diamond-lattice overlay StorySection uses.
        // The brown #634645 from <main> shows through the SVG's
        // empty interior; the subtle white-stroke diamond grid
        // sits on top.
        backgroundImage: "url('/images/diamond-tile.svg')",
        backgroundRepeat: 'repeat',
        backgroundSize: '64px 64px',
      }}
    >
      {/* Top spacer — fork-spoon decorative band. Mobile uses a
          thinner 40px band so the title block sits high in the
          section just below the header; from md+ the original
          100px height returns. */}
      <div
        aria-hidden="true"
        className="h-[40px] w-[90%] bg-[url('/images/fork-spoon.svg')] bg-[length:72px_24px] bg-center bg-repeat-x opacity-70 md:h-[100px]"
      />

      {/* Cream main panel ("scene"). The forced
          `min-h-[calc(100vh)]` and centred justification only
          apply at lg+ so the desktop layout still anchors a
          full-viewport scene. Below lg the panel collapses to
          its content's natural height and stacks content from
          the TOP (`justify-start`) — works with the
          mobile-specific `!min-h-screen` on the parent section
          to keep the section ≥ 100vh tall while the title sits
          high (just below the header via the title block's own
          `scroll-margin-top:60px`). Padding shrinks from
          `p-10 py-16` → `p-2 py-4` below lg so the book frame
          keeps more width on phones. */}
      <div className="relative isolate z-10 flex w-full max-w-[1874px] flex-col items-center justify-start overflow-hidden p-2 py-4 lg:min-h-[calc(100vh)] lg:justify-center lg:p-10 lg:py-16">
        {/* Section heading — Figma 120:901. Uses the
            campaign-specific title treatment (a copy of
            `register-title-shadow` carrying its own
            `--campaign-stroke-width` var so the campaign can
            drift independently of the other campaign-scene
            titles): white Kanit ExtraBold with extruded-stroke
            shadow, plus a smaller #FFC0D1 pink accent line below
            (StaffSection's titleAccent colour, stacked here per
            the Figma design instead of inline).

            Placement is order-driven so the same DOM element flips
            position responsively:
              • Mobile (default): `order-first` puts the title ABOVE
                the inner-frame wrapper, anchored to the top of the
                scene.
              • Desktop (lg+): `lg:order-last` drops it BELOW the
                wrapper. The inner block then constrains itself to
                `lg:w-1/2` so it aligns with the LEFT half of the
                recipe book (matching where the title sits in the
                Figma layout). `lg:mt-6` keeps the title clear of the
                floating chibi prop that hangs off the wrapper's
                bottom-left corner. */}
        <div
          // `snap-start` + `[scroll-margin-top:60px]` make this
          // mobile-only title block the section's snap target on
          // phones (mirrors Register / Story / Features mobile
          // snap setup). When mandatory snap settles, the title
          // lands 60px below the viewport top — just below the
          // ≈48px floating header — and the book frame + CTA +
          // prop stack naturally below in the section's flex
          // flow. `lg:hidden` already keeps this branch out of
          // the desktop layout, and the `lg:order-last` order
          // swap on desktop is unrelated to the snap target
          // (snap is disabled at md+ on <html> anyway).
          className="block lg:hidden order-first mx-auto w-full max-w-[calc(85vh_*_1.465)] px-2 snap-start [scroll-margin-top:60px] lg:order-last lg:mt-6"
        >
          <div className="text-center lg:w-1/2">
            <h2 className="campaign-title-shadow whitespace-nowrap font-display text-3xl font-extrabold text-white md:text-4xl lg:text-5xl xl:text-6xl">
              {t('campaign.heading')}
            </h2>
            <p
              className="campaign-title-shadow-accent mt-1 font-display text-base font-extrabold md:text-lg lg:text-xl xl:text-2xl"
              // `.campaign-title-shadow` sets color:#fff and lives
              // AFTER `@tailwind utilities` in index.css, so a
              // utility class like `text-[#FFC0D1]` would lose to
              // it on source-order. Inline `style` wins on
              // specificity, so the pink colour actually paints.
              style={{ color: '#FFC0D1' }}
            >
              {t('campaign.subtitle')}
            </p>
          </div>
        </div>
        {/* Inner content frame — `section6_inner_frame.png` painted as
            the rose/cream open-book background. Aspect-locked to the
            asset's native 2746×1874 (~1.465:1) so the painted book
            spine, page edges and corner decorations line up with the
            container at every breakpoint.

            Why BOTH `max-w-[calc(85vh*1.465)]` AND `max-h-[85vh]`:
              • With just `w-full` + `aspect-[...]` + `max-h-[85vh]`,
                short viewports clamp the height — but because
                `width: 100%` is also explicit, browsers preserve the
                wide width and let the aspect ratio break, leaving
                the container WIDER than the painted book image
                (which then letterboxes via `backgroundSize: contain`).
                The flex columns inside would extend past the painted
                page edges, defeating the whole 2-page layout.
              • Pinning `max-w` to `85vh × 1.465` makes the width hit
                its cap BEFORE the height does, so the container
                always shrinks proportionally and the aspect is
                preserved. The bg image then paints edge-to-edge
                and the flex columns line up with the actual painted
                pages at every viewport. `max-h-[85vh]` is kept as a
                belt-and-suspenders safety. */}
        {/* Inner-frame WRAPPER — sole purpose is to give the floating
            prop a positioning context that exactly matches the inner
            frame's painted box, but WITHOUT the inner frame's
            `overflow-hidden`. Carries the width cap, horizontal
            centring and outer margin the inner frame used to wear;
            the inner frame inside is now `w-full` and inherits those
            constraints. Wrapper has no aspect-ratio of its own — its
            height collapses to the inner frame's aspect-locked
            height — so `bottom-0 left-0` on the prop resolves to the
            same painted-book corner as before. */}
        <div className="relative mx-auto my-3 w-full max-w-[calc(85vh_*_1.465)] md:my-4">
          <div
            className="relative ps-[8%] pe-[7%] pt-[5%] pb-[8%] flex aspect-[2746/1874] max-h-[85vh] w-full flex-row overflow-hidden"
            style={{
              backgroundImage: "url('/images/section6_inner_frame.png')",
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          >
            {/* Left page text area — fills the left half of the frame.
                Padding is in % so the text always stays inside the
                painted page (avoiding the leather binding on the
                left edge and the central book spine on the right
                edge) regardless of frame size. Vertically centred. */}
            <div className="flex w-1/2 flex-col items-center justify-start px-[8%] py-[5%]">
              <div className="hidden lg:block mx-auto w-full flex flex-col items-center">
                <div className="text-center gap-0">
                  <h2 className="w-full campaign-title-shadow flex justify-center items-center whitespace-nowrap font-display text-5xl font-black 2xl:text-6xl text-white">
                    {t('campaign.heading')}
                  </h2>
                  <p
                    className="w-full text-nowrap campaign-title-shadow-accent mt-2 font-display text-2xl font-extrabold"
                    // `.campaign-title-shadow` sets color:#fff and lives
                    // AFTER `@tailwind utilities` in index.css, so a
                    // utility class like `text-[#FFC0D1]` would lose to
                    // it on source-order. Inline `style` wins on
                    // specificity, so the pink colour actually paints.
                    style={{ color: '#FFC0D1' }}
                  >
                    {t('campaign.subtitle')}
                  </p>
                </div>
              </div>
              <p className="lg:hidden overflow-hidden whitespace-pre-line text-center font-thai text-base font-medium leading-relaxed text-section-feature mt-6">
                {t('campaign.title')}
              </p>
              <p className="hidden lg:block overflow-hidden whitespace-pre-line text-center font-thai text-base font-medium leading-relaxed text-section-feature mt-6">
                {t('campaign.titleDesktop')}
              </p>
            </div>

            {/* Right page text area — mirrors the left page. Now also
                hosts the CTA button anchored to the lower-left corner
                of the page, so it's positioned `relative` to give the
                absolute button a positioning context. */}
            <div className="overflow-hidden relative flex w-1/2 flex-col items-center justify-start px-[8%] pt-[10%]">
              <p className="lg:hidden whitespace-pre-line text-center font-thai text-base font-medium leading-relaxed text-section-feature">
                {t('campaign.detail')}
              </p>
              <p className="overflow-hidden mb-[40%] hidden lg:block whitespace-pre-line text-center font-thai text-base font-medium leading-relaxed text-section-feature">
                {t('campaign.detailDesktop')}
              </p>

              {/* CTA "Join" button — Figma 120:1117 (desktop)
                  / 191:4961 (mobile). The export is a fully
                  pre-rendered PNG (323×71): brown rim, cream/gold
                  gradient interior, two flanking muffin icons and
                  the "เข้าร่วม" Kanit ExtraBold label all baked in.

                  This copy positions the button at the lower-left
                  of the right page (Figma desktop design) and is
                  hidden below lg via `hidden lg:block` — the
                  mobile design pulls the button OUTSIDE the book
                  frame, so a separate sibling-flow copy is
                  rendered after the frame for that layout. */}
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                aria-label={t('campaign.readMore')}
                className="absolute bottom-[8%] left-[8%] hidden w-[55%] transition hover:scale-105 active:scale-95 md:w-[50%] lg:block lg:w-[45%]"
              >
                <img
                  src="/images/campaign-join.png"
                  alt=""
                  aria-hidden="true"
                  className="block h-auto w-full select-none"
                  draggable={false}
                />
              </button>
            </div>
          </div>

          {/* Mobile-only "อ่านเพิ่มเติม" label — Figma 191:5058.
              Uses the campaign-title-shadow extruded treatment so
              it shares the same white-fill / brown-stroke / brown
              extruded-drop-shadow vocabulary as the section
              heading above the frame. `lg:hidden` because the
              desktop layout doesn't repeat this caption. */}
          {/* keep the code as is but make it hidden until we complete the campaign information later */}
          <p className="hidden campaign-readmore-pill mt-6 block text-center font-display text-xl font-extrabold lg:hidden">
            {t('campaign.readMore')}
          </p>

          {/* Mobile-only fork-spoon divider — Figma 191:5059. Same
              two-icon-flanking-a-thin-bar pattern used in
              RegisterSection / StorySection / StaffSection
              dividers; the assets and structure are identical so
              the campaign matches the rest of the site's visual
              language. Width is capped so the bar stays compact
              under the readMore label rather than spanning the
              full frame width. */}
          <div className="mx-auto mt-2 flex w-[80%] max-w-sm items-center justify-center gap-2 lg:hidden">
            <img
              src="/images/fork-spoon.svg"
              alt=""
              aria-hidden="true"
              className="h-6 w-6"
              draggable={false}
            />
            <div className="h-[2px] flex-1 rounded-full bg-tan" />
            <img
              src="/images/fork-spoon.svg"
              alt=""
              aria-hidden="true"
              className="h-6 w-6"
              draggable={false}
            />
          </div>

          {/* Mobile-only "Join" CTA — Figma 191:4961. Mirrors the
              desktop button above (same PNG, same modal handler,
              same aria-label, same hover/active scale) but lives
              OUTSIDE the book frame as a normal-flow centred
              block. Width caps a bit larger than the desktop
              variant (60%) so the bigger pre-rendered tablet/phone
              tap target is comfortable on small screens.
              `lg:hidden` keeps it out of the desktop layout where
              the absolute-in-right-page version takes over. */}
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            aria-label={t('campaign.readMore')}
            className="relative mx-auto mt-4 block w-[60%] max-w-[360px] transition hover:scale-105 active:scale-95 lg:hidden"
          >
            <img
              src="/images/campaign-join.png"
              alt=""
              aria-hidden="true"
              className="block h-auto w-full select-none"
              draggable={false}
            />
          </button>

          {/* Decorative chibi-picnic prop — `section6-prop00.png`,
              1248×896 ≈ 1.393:1. Position is responsive:
                • Mobile (default): renders as a normal-flow block
                  centred under the join button, ~80% of the
                  wrapper width — matches Figma 191:4961 where the
                  prop sits below the CTA rather than overlapping
                  the book.
                • lg+: `lg:absolute lg:bottom-12 lg:left-0` +
                  `lg:-translate-x-1/2 lg:translate-y-1/2` lifts the
                  prop out of flow and anchors it to the wrapper's
                  bottom-left corner, half in / half outside the
                  painted book — Figma desktop 120:901 layout.
              Wrapper handles the STATIC positioning; the `<img>`
              inside carries the `animate-float-y` loop so the
              floating animation composes with the centring
              translate without overwriting it. */}
          <div className="pointer-events-none relative z-10 mx-auto mt-4 block w-[80%] max-w-[460px] select-none lg:absolute lg:bottom-12 lg:left-0 lg:mt-0 lg:w-[30%] lg:max-w-none lg:-translate-x-1/2 lg:translate-y-1/2">
            <img
              src="/images/section6-prop00.png"
              alt=""
              aria-hidden="true"
              className="block w-full animate-float-y select-none"
              draggable={false}
            />
          </div>
        </div>
      </div>

      {/* Bottom spacer — mirrors the top spacer so the panel sits
          between two symmetrical decorative bands. */}
      <div
        aria-hidden="true"
        className="h-[80px] lg:h-[180px] w-[90%] bg-[url('/images/fork-spoon.svg')] bg-[length:72px_24px] bg-center bg-repeat-x opacity-70"
      />

      <JoinModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
}
